package com.crowpay.views.components.project.work.scope

import com.crowpay.LineItemState
import com.crowpay.ScopeView
import com.crowpay.utils.AdjustedLineItem
import com.crowpay.utils.renderPercent
import com.crowpay.utils.shareFilter
import com.crowpay.views.components.priceWithChange
import com.crowpay.views.components.scopeSetIcon
import com.crowpay.views.screens.common.HasProjectData
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.theming.LineItemStateSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.boldSubTitle
import com.crowpay.views.theming.lightSection
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.link
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.padded
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding

fun HasProjectData.lineItemCard(item: Readable<AdjustedLineItem>, lens: ProjectLens, writer: ViewWriter) = with(writer) {
    link {
        col {
            row {
                centered - expanding - boldSubTitle {
                    ::content { item().name }
                }
                centered - bodyBold {
                    spacing = 5.dp
                    ::exists { item().scopeSet != true }
                    dynamicTheme {
                        LineItemStateSemantic(item().state)
                    }
                    ::content { item().state.displayName }
                }
            }
            compact - row {
                row {
                    centered - body("Amount:")
                    centered - priceWithChange(
                        addPadding = false,
                        price = { item().wraps.price },
                        change = { item().wraps.let { it.price - it.originalPrice } }
                    )
                }
                expanding - space()
                row {
                    val paid = shared {
                        val line = item()
                        if (line.complete != null) return@shared line.wraps.price
                        payAppItems()
                            .filter { it.lineItem == line._id && (it.pending || it.accepted == true) }
                            .sumOf { it.amount }
                    }
                    centered - body("% Complete: ")
                    centered - body {
                        ::content {
                            val it = item()
                            if (it.cancelled) "-"
                            else (paid().toDouble()/item().wraps.price).renderPercent(1)
                        }
                    }
                }
            }
        }
        ::to { { LineItemDetailsPage(projectId, item()._id, lens, this@lineItemCard) } }
    }
}

fun ProjectView.scopeViewCard(scope: Readable<ScopeView>, writer: ViewWriter) = with(writer) {
    val items = lineItems.shareFilter { it.scopeView == scope()._id }
    val status = shared {
        if (scope().scopeSet) items().firstOrNull()?.state
        else null
    }

    lightSection - link {
        col {
            row {
                expanding - compact - row {
                    centered - onlyWhen { scope().scopeSet } - scopeSetIcon(1.2.rem)
                    centered - expanding - boldSubTitle {
                        ::content { scope().scopeTitle }
                    }
                }
                centered - bodyBold {
                    spacing = 5.dp
                    ::exists { status() != null }
                    dynamicTheme {
                        status()?.let(::LineItemStateSemantic)
                    }
                    ::content { status()?.displayName ?: "" }
                }
            }
            compact - row {
                row {
                    centered - body("Amount:")
                    centered - priceWithChange(
                        addPadding = false,
                        price = { items().sumOf { it.price } },
                        change = { items().sumOf { it.price - it.wraps.originalPrice } }
                    )
                }
                expanding - space()
                row {
                    val paid = shared {
                        val lines = items()
                        val draws = payAppItems()
                        lines.sumOf { line ->
                            if (line.complete != null) line.wraps.price
                            else draws
                                .filter { it.lineItem == line._id && (it.pending || it.accepted == true) }
                                .sumOf { it.amount }
                        }
                    }
                    centered - body("% Complete: ")
                    centered - body {
                        ::content {
                            if (status() == LineItemState.Cancelled) "-"
                            else (paid().toDouble() / items().sumOf { it.price }).renderPercent(1)
                        }
                    }
                }
            }
        }

        ::to { { ScopeViewDetails(projectId, scope()._id, lens, this@scopeViewCard) } }
    }
}